import { isNotFound, isRedirect, notFound } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { ORPCError } from "@orpc/server";
import { reportError, unexpectedError } from "../../../api/src/errors/report";
import { scrub } from "../../../api/src/errors/event";
import { safeErrorMessage } from "../../../api/src/db/errors";
import { siteRequest } from "../../../api/src/site-request";

/**
 * Route data never carries a server error's message to the browser, where Start would embed it
 * in the page (the Astro site's 500 page never showed one). A redirect or missing page passes
 * through; a service's not-found is the page's 404; anything else is reported (Admin › Errors,
 * when unexpected) and the page receives a plain error its error state explains.
 */
export const safeErrors = createMiddleware({ type: "function" }).server(
  async ({ next, context }) => {
    try {
      return await next();
    } catch (error) {
      if (isRedirect(error) || isNotFound(error)) throw error;
      if (error instanceof ORPCError && error.status === 404) throw notFound();
      const request = getRequest();
      // A publication render keeps what failed, even a validation error, which a visitor's
      // request treats as expected: a stored page the renderer rejects is a broken publication.
      const publication = siteRequest(request)?.publication;
      if (publication) publication.failure ??= scrub(safeErrorMessage(error)).slice(0, 300);
      if (unexpectedError(error)) {
        reportError({ env: context.env, executionCtx: context.executionCtx }, error, {
          source: "page",
          route: new URL(request.url).pathname,
          method: request.method,
        });
      }
      throw new Error("This page couldn't be loaded.");
    }
  },
);
